import { useState, useEffect, useRef } from 'react';
import api from '../../services/api';
import Card from '../../components/common/Card';
import Button from '../../components/common/Button';
import Input from '../../components/common/Input';
import Alert from '../../components/common/Alert';
import Spinner from '../../components/common/Spinner';

interface AIProvider {
  id: string;
  name: string;
  description: string;
  features: string[];
  pricing: string;
  recommended: boolean;
  free?: boolean;
  models: {
    vision: string[];
    text: string[];
    embedding: string[];
  };
}

interface AIConfig {
  id: string;
  provider: string;
  is_active: boolean;
  vision_model: string;
  text_model: string;
  embedding_model: string;
  temperature: number;
  max_tokens: number;
  top_p: number;
  top_k: number;
  total_requests: number;
  total_tokens_used: number;
  monthly_budget_usd: number | null;
  current_month_spend_usd: number;
  last_used_at: string | null;
  // vision provider split
  vision_provider: string | null;
  vision_embedding_model: string | null;
}

interface UsageStats {
  totalRequests: number;
  totalTokens: number;
  totalCost: number;
  successRate: number;
  avgProcessingTime: number;
  byRequestType: Record<string, { count: number; tokens: number; cost: number }>;
  byModel: Record<string, { count: number; tokens: number; cost: number }>;
}

// Providers that support vision / embeddings — used to filter the vision card
const VISION_CAPABLE  = ['gemini', 'openai', 'anthropic', 'azure-openai', 'openrouter', 'cloudflare'];
const EMBED_CAPABLE   = ['gemini', 'openai', 'azure-openai', 'cloudflare'];

export default function AIConfigurationPage() {
  const [providers, setProviders]       = useState<AIProvider[]>([]);
  const [configs, setConfigs]           = useState<AIConfig[]>([]);
  const [usageStats, setUsageStats]     = useState<UsageStats | null>(null);
  const [loading, setLoading]           = useState(true);
  const [saving, setSaving]             = useState(false);
  const [testingText, setTestingText]   = useState(false);
  const [testingVision, setTestingVision] = useState(false);
  const [error, setError]               = useState<string | null>(null);
  const [success, setSuccess]           = useState<string | null>(null);
  const alertRef                        = useRef<HTMLDivElement>(null);

  // ── Text provider form state ───────────────────────────────────────────────
  const [textProvider, setTextProvider]     = useState('groq');
  const [textApiKey, setTextApiKey]         = useState('');
  const [textModel, setTextModel]           = useState('');
  const [temperature, setTemperature]       = useState(0.7);
  const [maxTokens, setMaxTokens]           = useState(2048);
  const [topP, setTopP]                     = useState(0.95);
  const [monthlyBudget, setMonthlyBudget]   = useState<number | ''>('');

  // ── Vision / embedding provider form state ────────────────────────────────
  const [useSeparateVision, setUseSeparateVision] = useState(false);
  const [visionProvider, setVisionProvider]       = useState('gemini');
  const [visionApiKey, setVisionApiKey]           = useState('');
  const [visionModel, setVisionModel]             = useState('');
  const [embeddingModel, setEmbeddingModel]       = useState('');

  useEffect(() => { loadData(); }, []);

  // Scroll alert into view whenever a message is set.
  // block: 'center' ensures it clears any sticky navbar above it.
  useEffect(() => {
    if ((error || success) && alertRef.current) {
      alertRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }, [error, success]);

  const loadData = async () => {
    try {
      setLoading(true);
      setError(null);

      const providersRes = await api.get('/ai-config/providers');
      const providersData: AIProvider[] = providersRes.data;
      setProviders(providersData);

      const configsRes = await api.get('/ai-config/');
      const configsData: AIConfig[] = configsRes.data;
      setConfigs(configsData);

      try {
        const statsRes = await api.get('/ai-config/usage-stats');
        setUsageStats(statsRes.data);
      } catch { /* stats not yet available */ }

      // Pre-fill form from active config
      if (configsData.length > 0 && configsData[0].is_active) {
        const cfg = configsData[0];

        setTextProvider(cfg.provider);
        setTextModel(cfg.text_model);
        setTemperature(cfg.temperature);
        setMaxTokens(cfg.max_tokens);
        setTopP(cfg.top_p);
        setMonthlyBudget(cfg.monthly_budget_usd || '');

        if (cfg.vision_provider) {
          setUseSeparateVision(true);
          setVisionProvider(cfg.vision_provider);
          setVisionModel(cfg.vision_model);
          setEmbeddingModel(cfg.vision_embedding_model || '');
        } else {
          setUseSeparateVision(false);
          setVisionProvider('gemini');
          const gemini = providersData.find(p => p.id === 'gemini');
          setVisionModel(gemini?.models.vision[0] || '');
          setEmbeddingModel(gemini?.models.embedding[0] || '');
        }
      } else if (providersData.length > 0) {
        // Defaults: Groq for text, Gemini for vision
        const defaultText = providersData.find(p => p.recommended) || providersData[0];
        setTextProvider(defaultText.id);
        setTextModel(defaultText.models.text[0] || '');
        const gemini = providersData.find(p => p.id === 'gemini');
        if (gemini) {
          setVisionModel(gemini.models.vision[0] || '');
          setEmbeddingModel(gemini.models.embedding[0] || '');
        }
      }
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to load AI configuration');
    } finally {
      setLoading(false);
    }
  };

  const handleTextProviderChange = (id: string) => {
    setTextProvider(id);
    const p = providers.find(x => x.id === id);
    if (p) setTextModel(p.models.text[0] || '');
  };

  const handleVisionProviderChange = (id: string) => {
    setVisionProvider(id);
    const p = providers.find(x => x.id === id);
    if (p) {
      setVisionModel(p.models.vision[0] || '');
      setEmbeddingModel(p.models.embedding[0] || '');
    }
  };

  const handleTestText = async () => {
    if (!textApiKey) { setError('Enter a Text Provider API key first'); return; }
    try {
      setTestingText(true); setError(null); setSuccess(null);
      const res = await api.post('/ai-config/test-connection', { provider: textProvider, apiKey: textApiKey });
      if (res.data.success) setSuccess(`✅ Text provider connection successful! Latency: ${res.data.latencyMs}ms`);
      else setError(`❌ Text provider failed: ${res.data.error}`);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Text provider test failed');
    } finally { setTestingText(false); }
  };

  const handleTestVision = async () => {
    if (!visionApiKey) { setError('Enter a Vision Provider API key first'); return; }
    try {
      setTestingVision(true); setError(null); setSuccess(null);
      const res = await api.post('/ai-config/test-connection', { provider: visionProvider, apiKey: visionApiKey });
      if (res.data.success) setSuccess(`✅ Vision provider connection successful! Latency: ${res.data.latencyMs}ms`);
      else setError(`❌ Vision provider failed: ${res.data.error}`);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Vision provider test failed');
    } finally { setTestingVision(false); }
  };

  const handleSave = async () => {
    if (!textApiKey && !activeConfig) {
      setError('Enter a Text Provider API key');
      return;
    }
    if (useSeparateVision && !visionApiKey && !activeConfig?.vision_provider) {
      setError('Enter a Vision Provider API key');
      return;
    }

    try {
      setSaving(true); setError(null); setSuccess(null);

      const payload: any = {
        provider: textProvider,
        models:   { text: textModel },
        options:  { temperature, maxTokens, topP, monthlyBudgetUsd: monthlyBudget || undefined },
      };
      if (textApiKey) payload.apiKey = textApiKey;

      if (useSeparateVision) {
        payload.visionProvider = {
          provider:       visionProvider,
          visionModel:    visionModel,
          embeddingModel: embeddingModel,
        };
        if (visionApiKey) payload.visionProvider.apiKey = visionApiKey;
      }

      await api.post('/ai-config/', payload);
      setSuccess('✅ AI configuration saved successfully!');
      setTextApiKey('');
      setVisionApiKey('');
      await loadData();
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to save configuration');
    } finally { setSaving(false); }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Spinner size="lg" />
      </div>
    );
  }

  const activeConfig = configs.find(c => c.is_active);
  const textProviderData   = providers.find(p => p.id === textProvider);
  const visionProviderData = providers.find(p => p.id === visionProvider);

  // Only providers that have at least text models are valid for the text slot
  const textProviders   = providers;
  // Only providers that have vision or embedding models are shown in the vision slot
  const visionProviders = providers.filter(p => VISION_CAPABLE.includes(p.id));

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold text-gray-900 dark:text-white">AI Configuration</h1>
        <p className="mt-2 text-gray-600 dark:text-gray-400">
          Configure separate providers for text/chat and vision/embeddings so each capability
          uses the best available model.
        </p>
      </div>

      {/* Alerts — ref used to scroll into view when set from buttons at bottom of page */}
      <div ref={alertRef}>
        {error   && <Alert type="error"   onClose={() => setError(null)}>{error}</Alert>}
        {success && <Alert type="success" onClose={() => setSuccess(null)}>{success}</Alert>}
      </div>

      {/* Current active config summary */}
      {activeConfig && (
        <Card>
          <div className="p-6">
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">Active Configuration</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              <div>
                <p className="text-sm text-gray-500 dark:text-gray-400">Text / Chat Provider</p>
                <p className="text-lg font-medium text-gray-900 dark:text-white capitalize">{activeConfig.provider}</p>
                <p className="text-xs text-gray-500 dark:text-gray-400">{activeConfig.text_model}</p>
              </div>
              <div>
                <p className="text-sm text-gray-500 dark:text-gray-400">Vision / Embedding Provider</p>
                <p className="text-lg font-medium text-gray-900 dark:text-white capitalize">
                  {activeConfig.vision_provider || activeConfig.provider}
                </p>
                <p className="text-xs text-gray-500 dark:text-gray-400">{activeConfig.vision_model}</p>
              </div>
              <div>
                <p className="text-sm text-gray-500 dark:text-gray-400">Total Requests</p>
                <p className="text-lg font-medium text-gray-900 dark:text-white">
                  {activeConfig.total_requests.toLocaleString()}
                </p>
              </div>
              <div>
                <p className="text-sm text-gray-500 dark:text-gray-400">Monthly Spend</p>
                <p className="text-lg font-medium text-gray-900 dark:text-white">
                  ${activeConfig.current_month_spend_usd.toFixed(2)}
                  {activeConfig.monthly_budget_usd && (
                    <span className="text-sm text-gray-500"> / ${activeConfig.monthly_budget_usd}</span>
                  )}
                </p>
              </div>
            </div>
          </div>
        </Card>
      )}

      {/* ── Card 1: Text & Chat Provider ──────────────────────────────────────── */}
      <Card>
        <div className="p-6 space-y-5">
          <div>
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white">Text &amp; Chat Provider</h2>
            <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
              Used for NLQ, help chat, mission briefings, OGI analysis, and weather checks.
              Text-only capability is sufficient — Groq is the recommended free option.
            </p>
          </div>

          {/* Provider grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {textProviders.map(p => (
              <button
                key={p.id}
                onClick={() => handleTextProviderChange(p.id)}
                className={`p-4 border-2 rounded-lg text-left transition-colors ${
                  textProvider === p.id
                    ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20'
                    : 'border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600'
                }`}
              >
                <div className="flex items-start justify-between">
                  <div>
                    <h3 className="font-semibold text-gray-900 dark:text-white text-sm">
                      {p.name}
                      {p.free && (
                        <span className="ml-2 text-xs bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200 px-2 py-0.5 rounded">Free</span>
                      )}
                      {p.recommended && (
                        <span className="ml-2 text-xs bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200 px-2 py-0.5 rounded">Recommended</span>
                      )}
                    </h3>
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">{p.description}</p>
                  </div>
                </div>
              </button>
            ))}
          </div>

          {/* API key */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              API Key *
            </label>
            <Input
              type="password"
              value={textApiKey}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setTextApiKey(e.target.value)}
              placeholder={activeConfig ? 'Configured — enter new key to rotate' : 'Enter your API key'}
              className="w-full"
            />
            <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
              Encrypted before storage, never returned to the browser.
            </p>
          </div>

          {/* Text model + settings */}
          {textProviderData && (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Text Model</label>
                <select
                  value={textModel}
                  onChange={e => setTextModel(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-800 text-gray-900 dark:text-white text-sm"
                >
                  {textProviderData.models.text.map(m => (
                    <option key={m} value={m}>{m}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Temperature ({temperature})
                </label>
                <input type="range" min="0" max="2" step="0.1" value={temperature}
                  onChange={e => setTemperature(parseFloat(e.target.value))} className="w-full mt-2" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Max Tokens</label>
                <Input type="number" value={maxTokens}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => setMaxTokens(parseInt(e.target.value))}
                  min={100} max={32000} className="w-full" />
              </div>
            </div>
          )}

          {/* Budget + test */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-1">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Monthly Budget (USD) — Optional
              </label>
              <Input type="number" value={monthlyBudget}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                  setMonthlyBudget(e.target.value ? parseFloat(e.target.value) : '')}
                placeholder="No limit" min={0} className="w-full" />
            </div>
            <div className="flex items-end">
              <Button onClick={handleTestText} disabled={!textApiKey || testingText} variant="secondary">
                {testingText ? 'Testing…' : 'Test Text Connection'}
              </Button>
            </div>
          </div>
        </div>
      </Card>

      {/* ── Card 2: Vision & Embedding Provider ───────────────────────────────── */}
      <Card>
        <div className="p-6 space-y-5">
          <div className="flex items-start justify-between">
            <div>
              <h2 className="text-xl font-semibold text-gray-900 dark:text-white">Vision &amp; Embedding Provider</h2>
              <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                Used for satellite image analysis, crop health, OGI imagery, and vector search embeddings.
                When disabled the text provider handles all requests (vision will degrade to text-only on providers like Groq).
              </p>
            </div>
            {/* Toggle */}
            <button
              onClick={() => setUseSeparateVision(v => !v)}
              className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 focus:outline-none ${
                useSeparateVision ? 'bg-blue-600' : 'bg-gray-300 dark:bg-gray-600'
              }`}
              role="switch"
              aria-checked={useSeparateVision}
            >
              <span className={`pointer-events-none inline-block h-5 w-5 rounded-full bg-white shadow transform transition duration-200 ${
                useSeparateVision ? 'translate-x-5' : 'translate-x-0'
              }`} />
            </button>
          </div>

          {useSeparateVision && (
            <>
              {/* Provider grid — vision-capable only */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {visionProviders.map(p => (
                  <button
                    key={p.id}
                    onClick={() => handleVisionProviderChange(p.id)}
                    className={`p-4 border-2 rounded-lg text-left transition-colors ${
                      visionProvider === p.id
                        ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20'
                        : 'border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600'
                    }`}
                  >
                    <h3 className="font-semibold text-gray-900 dark:text-white text-sm">
                      {p.name}
                      {p.free && (
                        <span className="ml-2 text-xs bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200 px-2 py-0.5 rounded">Free</span>
                      )}
                    </h3>
                    <div className="flex flex-wrap gap-1 mt-1">
                      {p.features.map(f => (
                        <span key={f} className="text-xs bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400 px-2 py-0.5 rounded">{f}</span>
                      ))}
                    </div>
                  </button>
                ))}
              </div>

              {/* Vision API key */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Vision Provider API Key *
                </label>
                <Input
                  type="password"
                  value={visionApiKey}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => setVisionApiKey(e.target.value)}
                  placeholder={activeConfig?.vision_provider ? 'Configured — enter new key to rotate' : 'Enter vision provider API key'}
                  className="w-full"
                />
              </div>

              {/* Vision model + embedding model */}
              {visionProviderData && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Vision Model</label>
                    <select
                      value={visionModel}
                      onChange={e => setVisionModel(e.target.value)}
                      disabled={visionProviderData.models.vision.length === 0}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-800 text-gray-900 dark:text-white text-sm disabled:opacity-50"
                    >
                      {visionProviderData.models.vision.length > 0
                        ? visionProviderData.models.vision.map(m => <option key={m} value={m}>{m}</option>)
                        : <option value="">Not available</option>
                      }
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Embedding Model</label>
                    <select
                      value={embeddingModel}
                      onChange={e => setEmbeddingModel(e.target.value)}
                      disabled={visionProviderData.models.embedding.length === 0}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-800 text-gray-900 dark:text-white text-sm disabled:opacity-50"
                    >
                      {visionProviderData.models.embedding.length > 0
                        ? visionProviderData.models.embedding.map(m => <option key={m} value={m}>{m}</option>)
                        : <option value="">Not available</option>
                      }
                    </select>
                    {!EMBED_CAPABLE.includes(visionProvider) && (
                      <p className="mt-1 text-xs text-amber-600 dark:text-amber-400">
                        This provider does not support embeddings — vector search will fall back to the platform Gemini key.
                      </p>
                    )}
                  </div>
                </div>
              )}

              {/* Test vision */}
              <div>
                <Button onClick={handleTestVision} disabled={!visionApiKey || testingVision} variant="secondary">
                  {testingVision ? 'Testing…' : 'Test Vision Connection'}
                </Button>
              </div>
            </>
          )}

          {!useSeparateVision && (
            <p className="text-sm text-gray-500 dark:text-gray-400 italic">
              Vision and embeddings will use the same provider as Text &amp; Chat.
              {textProviderData && !VISION_CAPABLE.includes(textProvider) && (
                <span className="ml-1 text-amber-600 dark:text-amber-400">
                  Warning: <strong>{textProviderData.name}</strong> does not support vision — image analysis will run in text-only degraded mode.
                  Enable a separate Vision Provider above to fix this.
                </span>
              )}
            </p>
          )}
        </div>
      </Card>

      {/* Save */}
      <div className="flex justify-end">
        <Button
          onClick={handleSave}
          disabled={(!textApiKey && !activeConfig) || saving}
          variant="primary"
        >
          {saving ? 'Saving…' : (activeConfig ? 'Update Configuration' : 'Save Configuration')}
        </Button>
      </div>

      {/* Usage Statistics */}
      {usageStats && (
        <Card>
          <div className="p-6">
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">Usage Statistics</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              <div>
                <p className="text-sm text-gray-500 dark:text-gray-400">Total Requests</p>
                <p className="text-2xl font-bold text-gray-900 dark:text-white">{usageStats.totalRequests.toLocaleString()}</p>
              </div>
              <div>
                <p className="text-sm text-gray-500 dark:text-gray-400">Total Tokens</p>
                <p className="text-2xl font-bold text-gray-900 dark:text-white">{usageStats.totalTokens.toLocaleString()}</p>
              </div>
              <div>
                <p className="text-sm text-gray-500 dark:text-gray-400">Total Cost</p>
                <p className="text-2xl font-bold text-gray-900 dark:text-white">${usageStats.totalCost.toFixed(2)}</p>
              </div>
              <div>
                <p className="text-sm text-gray-500 dark:text-gray-400">Success Rate</p>
                <p className="text-2xl font-bold text-gray-900 dark:text-white">{usageStats.successRate.toFixed(1)}%</p>
              </div>
            </div>

            {Object.keys(usageStats.byRequestType).length > 0 && (
              <div className="mt-6">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-3">By Request Type</h3>
                <div className="space-y-2">
                  {Object.entries(usageStats.byRequestType).map(([type, stats]) => (
                    <div key={type} className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-800 rounded">
                      <span className="font-medium text-gray-900 dark:text-white capitalize">{type.replace('_', ' ')}</span>
                      <div className="flex gap-4 text-sm text-gray-600 dark:text-gray-400">
                        <span>{stats.count} requests</span>
                        <span>{stats.tokens.toLocaleString()} tokens</span>
                        <span>${stats.cost.toFixed(2)}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </Card>
      )}
    </div>
  );
}

// Made with Bob
